import { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';

interface WalletChargeTransaction {
  id: string;
  time: string;
  description: string;
  card_brand?: string;
  card_last4?: string;
  status: 'succeeded' | 'completed' | 'pending' | 'failed' | 'canceled';
  amount: number;
  attempted_amount?: number;
  transaction_type: 'top_up' | 'admin_credit' | 'admin_debit' | 'transfer_in';
}

interface WalletChargeTransactionsProps {
  locationId: string;
  category: string;
  date: string;
  onBack: () => void;
  onBackToSummary: () => void;
}

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

// Status badge styles
const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  succeeded: { bg: 'rgba(34, 197, 94, 0.15)', text: '#22c55e' },
  completed: { bg: 'rgba(59, 130, 246, 0.15)', text: '#3b82f6' },
  pending: { bg: 'rgba(251, 191, 36, 0.15)', text: '#d97706' },
  failed: { bg: 'rgba(239, 68, 68, 0.15)', text: '#ef4444' },
  canceled: { bg: 'rgba(156, 163, 175, 0.15)', text: '#6b7280' },
};

/**
 * Wallet Charge Transactions View (Level 3)
 * Shows individual wallet charge transactions for a specific category and date
 */
export function WalletChargeTransactions({
  locationId,
  category,
  date,
  onBack,
  onBackToSummary,
}: WalletChargeTransactionsProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<WalletChargeTransaction[]>([]);
  const [displayName, setDisplayName] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  // Pagination state
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/wallet-charges-transactions?category=${category}&date=${date}`,
        { credentials: 'include' }
      );
      const data = (await response.json()) as {
        success: boolean;
        displayName?: string;
        transactions?: WalletChargeTransaction[];
        total_count?: number;
        total_amount?: number;
        error?: string;
      };
      if (data.success) {
        setTransactions(data.transactions || []);
        setDisplayName(data.displayName || category);
        setTotalCount(data.total_count || 0);
        setTotalAmount(data.total_amount || 0);
      } else {
        setError(data.error || 'Failed to load transactions');
      }
    } catch (err) {
      console.error('[WalletChargeTransactions] Fetch error:', err);
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [locationId, category, date]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Reset to page 1 when data or rowsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [transactions.length, rowsPerPage]);

  // Pagination calculations
  const totalPages = Math.ceil(transactions.length / rowsPerPage);
  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return transactions.slice(startIndex, startIndex + rowsPerPage);
  }, [transactions, currentPage, rowsPerPage]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (timeStr: string) => {
    const d = new Date(timeStr);
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const formatCurrency = (amount: number) => {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(Math.abs(amount));
    return amount >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  const formatCurrencyTotal = (amount: number) => {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(Math.abs(amount));
    return amount >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  const formatCount = (count: number) => {
    return count.toLocaleString();
  };

  const formatCardInfo = (tx: WalletChargeTransaction) => {
    if (tx.card_brand && tx.card_last4) {
      const brand = tx.card_brand.charAt(0).toUpperCase() + tx.card_brand.slice(1).toLowerCase();
      return `${brand} ****${tx.card_last4}`;
    }
    return '-';
  };

  const formatDescription = (tx: WalletChargeTransaction) => {
    // For failed transactions, append the attempted amount
    if (tx.status === 'failed' && tx.attempted_amount) {
      const attemptedFormatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      }).format(tx.attempted_amount);
      return `${tx.description} (Attempted: ${attemptedFormatted})`;
    }
    return tx.description;
  };

  const getStatusStyle = (status: string) => {
    return STATUS_STYLES[status.toLowerCase()] || STATUS_STYLES.pending;
  };

  const capitalizeStatus = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const getAmountColor = (tx: WalletChargeTransaction) => {
    if (tx.status === 'failed') return 'text-red-600';
    if (tx.transaction_type === 'admin_debit' || tx.amount < 0) return 'text-red-600';
    return 'text-green-600';
  };

  const getDisplayAmount = (tx: WalletChargeTransaction) => {
    // Failed transactions show $0.0000
    if (tx.status === 'failed') {
      return '$0.0000';
    }
    return formatCurrency(tx.amount);
  };

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchTransactions}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header with Title and Breadcrumb */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-gray-900">Individual Transactions</h2>
        <nav className="text-sm">
          <button
            onClick={onBackToSummary}
            className="text-blue-600 hover:underline"
          >
            Wallet Charges
          </button>
          <span className="mx-2 text-gray-400">&rsaquo;</span>
          <button
            onClick={onBack}
            className="text-blue-600 hover:underline"
          >
            {displayName}
          </button>
          <span className="mx-2 text-gray-400">&rsaquo;</span>
          <span className="font-semibold text-gray-900">{formatDate(date)}</span>
        </nav>
      </div>

      {/* Context Header (Blue Box) */}
      <div className="bg-sky-50 border-2 border-sky-500 rounded-lg p-4 mb-4 flex justify-between items-center">
        <div>
          <h3 className="text-base font-bold text-sky-700">
            {displayName} - {formatDate(date)}
          </h3>
          <p className="text-sm text-gray-600">
            {formatCount(totalCount)} transactions = {formatCurrencyTotal(totalAmount)}
          </p>
        </div>
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-gray-50 rounded text-sm font-medium hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Daily
        </button>
      </div>

      {/* Controls Row */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">Show</span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {ROWS_PER_PAGE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <span className="text-sm font-semibold text-gray-700">rows</span>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                Time
              </th>
              <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                Description
              </th>
              <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                Card
              </th>
              <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                Status
              </th>
              <th className="px-5 py-3 text-right text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedTransactions.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-gray-500">
                  No transactions for this date
                </td>
              </tr>
            ) : (
              paginatedTransactions.map((tx) => {
                const statusStyle = getStatusStyle(tx.status);
                return (
                  <tr key={tx.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {formatTime(tx.time)}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {formatDescription(tx)}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {formatCardInfo(tx)}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm">
                      <span
                        className="px-2.5 py-1 rounded text-xs font-semibold"
                        style={{
                          backgroundColor: statusStyle.bg,
                          color: statusStyle.text,
                        }}
                      >
                        {capitalizeStatus(tx.status)}
                      </span>
                    </td>
                    <td className={`px-5 py-3 border-b border-gray-200 text-sm text-right font-medium ${getAmountColor(tx)}`}>
                      {getDisplayAmount(tx)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {transactions.length > 0 && (
        <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {Math.min((currentPage - 1) * rowsPerPage + 1, transactions.length)} -{' '}
            {Math.min(currentPage * rowsPerPage, transactions.length)} of {transactions.length} transactions
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {getPageNumbers().map((page, idx) =>
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-3 py-1.5 text-sm text-gray-500">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page as number)}
                  className={`px-3 py-1.5 border rounded text-sm font-medium ${
                    currentPage === page
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              )
            )}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
